import PropTypes from "prop-types";
import * as React from "react";
import { sendEventToGTM } from "helpers/analytics";
import { translate, Interpolate } from "react-i18next";
import get from "lodash/get";

import settings from "settings";

import { clanCost } from "helpers/clan";
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from "helpers/datetime";
import { x } from "helpers/localization";
import { ClanInputWrapper, Center, RulesDialog } from "UIKit";

import { ButtonDublon, Divider, Gold, ErrorDublon, Icon, Spinner, Toggle, StepCSSTransitionGroup, DivTooltip, TooltipBody } from "@wg/wows-react-uikit";

import { ATTRIBUTES_VALIDATION_STATE } from "./Constants";

import styles from "./ClanCreate.scss";

const propTypes = {
  tag: PropTypes.shape({
    value: PropTypes.string.isRequired,
    isValidating: PropTypes.bool.isRequired,
    available: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired,
  name: PropTypes.shape({
    value: PropTypes.string.isRequired,
    isValidating: PropTypes.bool.isRequired,
    available: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired,
  currentAccount: PropTypes.shape({
    totalGold: PropTypes.number.isRequired,
    inClanCooldownTill: PropTypes.string
  }).isRequired,
  isProcessing: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  clanRulesContent: PropTypes.object,

  error: PropTypes.object,

  onHideDialog: PropTypes.func.isRequired,
  onShowDialog: PropTypes.func.isRequired,
  onUpdateFieldValue: PropTypes.func.isRequired,
  onValidateOnServer: PropTypes.func.isRequired,
  onCreateClanClick: PropTypes.func.isRequired,

  t: PropTypes.func.isRequired
};

class ClanCreate extends React.Component {

  constructor(props) {
    super(props);

    this.renderFooter = this.renderFooter.bind(this);
    this.onClickClansRules = this.onClickClansRules.bind(this);
    this.onCreateClanClick = this.onCreateClanClick.bind(this);
  }

  onClickClansRules() {
    sendEventToGTM({
      category: 'Create page',
      action: 'Click: Show rules dialog',
      label: 'Clan rules action link'
    });

    const RulesDialogContent = <RulesDialog data={this.props.clanRulesContent} onCloseClick={this.props.onHideDialog} />;
    this.props.onShowDialog(RulesDialogContent, 'large');
  }

  onCreateClanClick() {
    sendEventToGTM({
      category: 'Create page',
      action: 'Click: Create clan button',
      label: 'Create clan button in create page'
    });

    this.props.onCreateClanClick();
  }

  renderFooter() {
    const t = this.props.t;
    const account = this.props.currentAccount;
    const error = this.props.error || {};

    const cost = clanCost();
    const isDiscount = settings.clanCreate.cost > settings.clanCreate.cost - settings.clanCreate.discount;

    let footer = null;
    if (this.props.isProcessing) {
      footer = <Spinner />;
    } else if (account.inClanCooldownTill) {
      const tooltipContent = t('Вы недавно покинули клан. Создать свой клан можно не ранее %(date)s, %(time)s', {
        date: isoToFormattedLocalDate(account.inClanCooldownTill),
        time: isoToFormattedLocalTime(account.inClanCooldownTill)
      });
      footer = <DivTooltip tooltipBody={<TooltipBody>{tooltipContent}</TooltipBody>}>
                    <ButtonDublon caption={t('Создать клан:кнопка создания клана')} amount={cost} isDisabled isDiscount />
                </DivTooltip>;
    } else {
      let needGold = 0;
      if (get(error, 'additional_info.reason') === 'not_enough_gold') {
        needGold = error.additional_info.amount;
      }

      if (needGold === 0) {
        let tooltipContent = null;
        let isDisabled = this.props.tag.available !== ATTRIBUTES_VALIDATION_STATE.AVAILABLE || this.props.name.available !== ATTRIBUTES_VALIDATION_STATE.AVAILABLE;

        const anyValidationError = this.props.tag.error || this.props.name.error;
        if (anyValidationError || !this.props.tag.value || !this.props.name.value) {
          isDisabled = true;
          tooltipContent = <TooltipBody>{t('Введите уникальные тег и название, удовлетворяющие Правилам кланов.')}</TooltipBody>;
        } else if (this.props.tag.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE || this.props.name.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE) {
          tooltipContent = <TooltipBody>{t('Клан с таким тегом или названием уже зарегистрирован.')}</TooltipBody>;
        } else if (isDiscount) {
          tooltipContent = <TooltipBody>
                            <div className={styles.discountTooltip}>
                                <div className={styles.discountTooltipHead}>
                                    <Icon glyph="discount" />
                                    {t('Акция:tooltip')}
                                </div>
                                <div>
                                    <span className={styles.discountTooltipPriceText}>{t('Стоимость покупки:tooltip')}</span>
                                    <Gold amount={cost} />
                                </div>
                                <div className={styles.discountTooltipPriceCostWrap}>
                                    <span className={styles.discountTooltipPriceCost}>
                                        {settings.clanCreate.cost}
                                    </span>
                                </div>
                            </div>
                        </TooltipBody>;
        }

        footer = <div data-staff-name="create_create-clan">
                        <DivTooltip tooltipBody={tooltipContent}>
                            <ButtonDublon caption={t('Создать клан:кнопка создания клана')} amount={cost} isDisabled={isDisabled} isDiscount={isDiscount} onClick={this.onCreateClanClick} />
                        </DivTooltip>
                    </div>;
      } else {
        footer = <ErrorDublon caption={t('Стоимость создания')} amount={cost} deficit={needGold} />;
      }
    }
    return footer;
  }

  render() {
    const t = this.props.t;
    const inputsErrors = {
      tag: get(this.props.error, 'errors.tag', [])[0] || null,
      name: get(this.props.error, 'errors.name', [])[0] || null
    };

    const clanRulesComponent = <span data-staff-name="create_clan-rules">
                <Toggle caption={t('Правилами кланов')} onClick={this.onClickClansRules} />
            </span>;

    const footer = this.renderFooter();

    return <div className={styles.clanCreate}>
                <StepCSSTransitionGroup level={1}>
                    <div key="input-tag" className={styles.inputTag}>
                        <ClanInputWrapper isFocusOn field="tag" value={this.props.tag.value} isDisabled={this.props.isProcessing} isValidating={this.props.tag.isValidating} error={inputsErrors.tag || this.props.tag.error} onChange={this.props.onUpdateFieldValue} onValidateOnServer={this.props.onValidateOnServer} />
                    </div>
                </StepCSSTransitionGroup>
                <StepCSSTransitionGroup level={2}>
                    <div key="input-name" className={styles.inputName}>
                        <ClanInputWrapper field="name" value={this.props.name.value} isDisabled={this.props.isProcessing} isValidating={this.props.name.isValidating} error={inputsErrors.name || this.props.name.error} onChange={this.props.onUpdateFieldValue} onValidateOnServer={this.props.onValidateOnServer} />
                    </div>
                </StepCSSTransitionGroup>
                <StepCSSTransitionGroup level={3}>
                    <Divider key="divider-top" type="major" />
                    <div key="message" className={styles.message}>
                        {t('Вы сможете изменять название после создания клана, нажав на тег и название на экране «Мой клан».')}
                        &ensp;
                        {t('Изменение тега и названия осуществляется за дублоны.')}
                    </div>
                    <Divider key="divider-bottom" type="major" />
                </StepCSSTransitionGroup>
                <StepCSSTransitionGroup level={4}>
                    <div key="rules" className={styles.rules}>
                        <Interpolate i18nKey={x('Создавая клан, вы соглашаетесь с %(clanRules)s')} clanRules={clanRulesComponent} />
                    </div>
                    <Center key="footer">{footer}</Center>
                </StepCSSTransitionGroup>
            </div>;
  }
}

ClanCreate.propTypes = propTypes;

export default translate()(ClanCreate);